/**
 * UNEVIDENCED-FIGURE CHECKING — shared by paid ad copy
 * (`src/lib/ad-copy/generate-grounded-ad-copy.ts`) and the organic Brief
 * companion post (`src/lib/ai-agent/tools/generate-content.ts`, Lane R2-3).
 *
 * Moved out of `generate-grounded-ad-copy.ts` so there is exactly ONE place
 * that extracts figures from text and checks them against an evidence
 * corpus — never a second copy that could drift. `generate-grounded-ad-copy`
 * re-exports `extractFigures`/`findUnevidencedFigures` from here rather than
 * defining its own, so existing imports of those two names keep working
 * unchanged.
 */

const SPELLED_NUMBERS: Readonly<Record<string, number>> = {
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  fifteen: 15,
  twenty: 20,
  thirty: 30,
  forty: 40,
  fifty: 50,
  sixty: 60,
  seventy: 70,
  eighty: 80,
  ninety: 90,
  hundred: 100,
  thousand: 1000,
  dozen: 12,
  /**
   * Added 2026-09-24 (Lane R2-3) — the actual hallucination that motivated
   * moving this logic into a shared module: `generate_content`'s organic
   * companion post asserted "billions in data center investment" and this
   * map, lacking `million`/`billion`/`trillion`, could not see it. A magnitude
   * word is exactly as checkable and exactly as disprovable as "thirty".
   */
  million: 1000000,
  billion: 1000000000,
  trillion: 1000000000000,
};

const DIGIT_FIGURE =
  /(\d[\d,]*(?:\.\d+)?)(\s?(?:trillion|billion|million|thousand|bn|mm|[kmb])\b)?/gi;

/**
 * Every figure in the copy, whether written in digits or spelled out.
 * A URL (its date path segments) and a #hashtag are not claims, so both are
 * ignored — a caption's source link once tripped the organic figure strip.
 *
 * The spelled-out half is the point — a model that writes "thirty scheduled
 * posts" produces a countable, checkable, completely unevidenced number that
 * a digit-only regex reads as prose. "one" is excluded deliberately — in
 * English it is an article far more often than a count ("one afternoon",
 * "one place"), and flagging it would make this advisory noise. A trailing
 * "s" ("billions", "thousands") is stripped before lookup so the plural form
 * — the one that actually shows up in prose ("billions in investment") — is
 * caught too, without adding a second key for every entry above.
 */
export function extractFigures(rawText: string): number[] {
  const figures: number[] = [];
  const text = rawText.replace(/https?:\/\/\S+/gi, ' ').replace(/(?:^|\s)#[\p{L}\p{N}_]+/gu, ' ');
  // One value per money form: "$6M", "$6 million" and "$6,000,000" are the same figure (its
  // mantissa, 6), so a scale word is consumed with its number and never read as a second figure.
  const consumed: Array<[number, number]> = [];
  for (const match of text.matchAll(DIGIT_FIGURE)) {
    const mantissa = Number(match[1]!.replace(/,/g, ''));
    if (!Number.isFinite(mantissa)) continue;
    if (match[2]) {
      consumed.push([match.index! + match[1]!.length, match.index! + match[0].length]);
      figures.push(mantissa);
      continue;
    }
    figures.push(mantissa >= 1e6 && mantissa % 1e5 === 0 ? mantissa / 1e6 : mantissa);
  }
  for (const match of text.toLowerCase().matchAll(/[a-z]+/g)) {
    if (consumed.some(([start, end]) => match.index! >= start && match.index! < end)) continue;
    const word = match[0];
    const value =
      SPELLED_NUMBERS[word] ??
      (word.endsWith('s') ? SPELLED_NUMBERS[word.slice(0, -1)] : undefined);
    if (value !== undefined) figures.push(value);
  }
  return figures;
}

/**
 * True when `figure` is written in `text` as a money amount or a percentage ("$6M",
 * "$6 million", "40%"), not as a bare count ("2 weeks", "(x3)"). A hedge about a reporting
 * period applies to a money or percent claim; a bare digit that happens to equal one in an
 * unrelated tile ("$2M. Per Year (x3)") must not borrow that tile's hedge.
 */
export function figureIsMoneyOrPercent(figure: number, text: string): boolean {
  for (const match of text.matchAll(DIGIT_FIGURE)) {
    const mantissa = Number(match[1]!.replace(/,/g, ''));
    const value = mantissa >= 1e6 && mantissa % 1e5 === 0 && !match[2] ? mantissa / 1e6 : mantissa;
    if (value !== figure) continue;
    const before = text.slice(Math.max(0, match.index! - 2), match.index!);
    const after = text.slice(match.index! + match[0].length, match.index! + match[0].length + 2);
    if (match[2] || /[$£€]\s?$/.test(before) || /^\s?(?:%|percent)/i.test(after)) return true;
  }
  return false;
}

const SCALE_VALUES: Readonly<Record<string, number>> = {
  trillion: 1e12,
  billion: 1e9,
  bn: 1e9,
  b: 1e9,
  million: 1e6,
  mm: 1e6,
  m: 1e6,
  thousand: 1e3,
  k: 1e3,
};

/** A money amount or percentage as written in a text, with its position and its absolute value. */
export interface MoneyOrPercentSpan {
  readonly kind: 'money' | 'percent';
  /** Absolute value: "$6M", "$6 million" and "$6,000,000" are all 6000000; "40%" is 40. */
  readonly value: number;
  /** The figure as written, with its currency sign or percent sign ("$6M", "40%"). */
  readonly text: string;
  readonly start: number;
  readonly end: number;
}

/**
 * Every money amount and percentage in `text`, compared as what they are: "$6M" is six million
 * dollars, not the digit 6, so it never collides with "$6" or "6 weeks". A bare count ("2 weeks",
 * "(x3)") is not a span.
 */
export function moneyOrPercentSpans(rawText: string): MoneyOrPercentSpan[] {
  const spans: MoneyOrPercentSpan[] = [];
  for (const match of rawText.matchAll(DIGIT_FIGURE)) {
    const mantissa = Number(match[1]!.replace(/,/g, ''));
    if (!Number.isFinite(mantissa)) continue;
    const scale = match[2] ? (SCALE_VALUES[match[2].trim().toLowerCase()] ?? 1) : 1;
    let start = match.index!;
    let end = start + match[0].length;
    const before = rawText.slice(Math.max(0, start - 2), start);
    const after = rawText.slice(end, end + 8);
    const currency = /[$£€]\s?$/.exec(before);
    const percent = /^\s?(?:%|percent)/i.exec(after);
    if (currency) {
      start -= currency[0].length;
      spans.push({
        kind: 'money',
        value: mantissa * scale,
        text: rawText.slice(start, end),
        start,
        end,
      });
    } else if (percent) {
      end += percent[0].length;
      spans.push({
        kind: 'percent',
        value: mantissa,
        text: rawText.slice(start, end).replace(/\s+%/, '%'),
        start,
        end,
      });
    } else if (match[2]) {
      spans.push({ kind: 'money', value: mantissa * scale, text: match[0], start, end });
    }
  }
  return spans;
}

/** One field of shipping copy, ready to run the figure check over. */
export interface FigureCheckField {
  readonly field: string;
  readonly text: string;
}

/** A found figure with no support anywhere in the caller-authorized evidence. */
export interface UnevidencedFigureFinding {
  readonly field: 'unevidenced_figure';
  readonly detail: string;
  readonly provenance: string;
}

/**
 * Reports every figure in `fields` that does not appear anywhere in
 * `evidenceCorpus`.
 *
 * This is an ADVISORY-shaped finding, not a hard block — a figure about the
 * reader's own situation ("four weeks of invoices") is legitimate copy, not
 * a product claim, and a caller that treated every number as disqualifying
 * would fight its own brief. What every caller wants is every number sourced
 * before it ships, which is what this gives them; whether that becomes a
 * `needs_review` flag, a stripped sentence, or a plain advisory line is the
 * caller's call, not this function's.
 */
export function findUnevidencedFigures(
  fields: readonly FigureCheckField[],
  evidenceCorpus: string,
  provenance: string,
): UnevidencedFigureFinding[] {
  const evidenced = new Set(extractFigures(evidenceCorpus));
  const findings: UnevidencedFigureFinding[] = [];
  const seen = new Set<number>();
  for (const { field, text } of fields) {
    if (!text) continue;
    for (const figure of extractFigures(text)) {
      if (evidenced.has(figure) || seen.has(figure)) continue;
      seen.add(figure);
      findings.push({
        field: 'unevidenced_figure',
        detail: `The copy uses the figure "${figure}" (in ${field}) and no figure matching it appears in the evidence on file. If it describes what the product produces or saves, it is unsourced and a reader can disprove it; if it describes the reader's own week, it is fine as written.`,
        provenance,
      });
    }
  }
  return findings;
}

/**
 * Drops any SENTENCE in `text` that carries a figure not supported by
 * `evidenceCorpus`, rather than merely flagging it — for a surface (the
 * organic companion post, Lane R2-3) that ships first-person prose directly
 * to the reader with no human review step in between, an advisory is not
 * enough: the unsupported claim must not be there to read.
 *
 * A sentence boundary is `. `/`! `/`? ` — good enough for generated social
 * copy, which does not carry abbreviations or decimal-adjacent periods in
 * practice. A sentence with NO figures at all is always kept.
 */
export function stripUnevidencedFigureSentences(
  text: string,
  evidenceCorpus: string,
): { readonly text: string; readonly droppedSentences: readonly string[] } {
  const evidenced = new Set(extractFigures(evidenceCorpus));
  const sentences = text.split(/(?<=[.!?])\s+/).filter((s) => s.length > 0);
  const kept: string[] = [];
  const dropped: string[] = [];
  for (const sentence of sentences) {
    const figures = extractFigures(sentence);
    const hasUnevidenced = figures.some((figure) => !evidenced.has(figure));
    if (hasUnevidenced) {
      dropped.push(sentence.trim());
    } else {
      kept.push(sentence);
    }
  }
  return { text: kept.join(' ').trim(), droppedSentences: dropped };
}
